import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { ImpersonationResponse } from "@harmonyso/auth-sdk";
import { Copy, Check, Eye, EyeOff } from "lucide-react";

interface JwtDisplayDialogProps {
  isOpen: boolean;
  onClose: () => void;
  impersonationResult: ImpersonationResponse | null;
}

export function JwtDisplayDialog({
  isOpen,
  onClose,
  impersonationResult,
}: JwtDisplayDialogProps) {
  const [copied, setCopied] = useState(false);
  const [showFullJwt, setShowFullJwt] = useState(false);

  // Reset state when dialog opens with new impersonation result
  useEffect(() => {
    if (isOpen && impersonationResult) {
      setCopied(false);
      setShowFullJwt(false);
    }
  }, [isOpen, impersonationResult]);

  const handleCopy = async () => {
    if (impersonationResult?.jwt) {
      await navigator.clipboard.writeText(impersonationResult.jwt);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  };

  const truncateJwt = (jwt: string) => {
    if (jwt.length <= 200) return jwt;
    return `${jwt.substring(0, 100)}\n...\n${jwt.substring(jwt.length - 100)}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[80vh] overflow-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Impersonation Success
          </DialogTitle>
          <DialogDescription>
            Successfully generated impersonation JWT token. This is for
            debugging purposes.
          </DialogDescription>
        </DialogHeader>

        {impersonationResult && (
          <div className="space-y-4">
            {/* User Details */}
            <div className="space-y-2">
              <h4 className="font-medium">Impersonated User Details</h4>
              <div className="bg-muted/50 grid grid-cols-[120px_1fr] gap-2 rounded-lg p-3 text-sm">
                <span className="text-muted-foreground font-medium">
                  Tenant ID:
                </span>
                <span className="font-mono">
                  {impersonationResult.impersonated_user.tenant_id}
                </span>
                <span className="text-muted-foreground font-medium">
                  Login ID:
                </span>
                <span className="font-mono">
                  {impersonationResult.impersonated_user.login_id}
                </span>
              </div>
            </div>

            {/* JWT Token */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">JWT Token</h4>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowFullJwt(!showFullJwt);
                    }}
                  >
                    {showFullJwt ? (
                      <EyeOff className="mr-2 h-4 w-4" />
                    ) : (
                      <Eye className="mr-2 h-4 w-4" />
                    )}
                    {showFullJwt ? "Hide" : "Show"} Full Token
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      void handleCopy();
                    }}
                    disabled={copied}
                  >
                    {copied ? (
                      <>
                        <Check className="mr-2 h-4 w-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="mr-2 h-4 w-4" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <div className="max-h-48 overflow-auto rounded-lg bg-black p-4 font-mono text-xs text-green-400">
                <div className="whitespace-pre-wrap break-all">
                  {showFullJwt
                    ? impersonationResult.jwt
                    : truncateJwt(impersonationResult.jwt)}
                </div>
              </div>
              {!showFullJwt && (
                <p className="text-muted-foreground text-xs">
                  Token is truncated for display. Click "Show Full Token" to see
                  the complete JWT.
                </p>
              )}
            </div>

            <div className="flex justify-end">
              <Button onClick={onClose}>Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
